/**
 * **Die magnetische Zeitleiste** (7.201).
 *
 * Sascha, 4. Oktober: „Bitte eine magnetische zeitachse bauen und
 * aktivieren.“ Gemeint ist, was Final Cut Pro so nennt: die Hauptspur hat
 * keine Lücken -- was eingefügt, gekürzt, gelöscht oder verschoben wird,
 * schiebt den Rest weg oder zieht ihn zusammen --, und was auf den anderen
 * Spuren über einem Clip der Hauptspur liegt (B-Roll, Titel, Ton), hängt an
 * ihm und wandert mit.
 *
 * Gerechnet wird **nach** einer Änderung, aus dem Stand davor und danach,
 * und heraus kommen gewöhnliche `moveClip`-Schritte. Die App hängt sie an die
 * Änderung an: ein Rückgängig nimmt beides zurück, der Server sieht nichts
 * Neues, und Zwischenschritte (Teilen ist „kürzen, dann anlegen“) stören
 * nicht, weil nur der Endstand zählt.
 *
 * - **Hauptspur** ist die erste Bildspur. Ihre Clips liegen in der Reihenfolge
 *   ihrer Anfänge hintereinander, der erste bei null. Ein Clip mit Blende
 *   überlappt den vorigen um deren Länge (7.89: der Übergang *ist* die
 *   Überlappung). Wer einen Clip der Hauptspur an eine andere Stelle zieht,
 *   ordnet damit um.
 * - **Angehängt** ist ein Clip einer anderen Spur, der vorher über einem
 *   Clip der Hauptspur anfing. Er wandert um so viel, wie dieser gewandert
 *   ist. Was die Änderung selbst bewegt hat, bleibt, wo sie es hingelegt hat.
 * - Gesperrte Spuren bewegt der Magnet nicht; ist die Hauptspur gesperrt,
 *   tut er nichts.
 */
export function magnetZug(vorher, nachher) {
    if (!nachher.settings.magnetisch)
        return [];
    const basis = nachher.tracks.find((t) => t.kind === 'video');
    if (!basis || basis.locked)
        return [];
    // **Hat die Änderung die Hauptspur nicht angefasst, bleibt sie, wie sie
    // ist** (Sascha, 16. Oktober: „Wenn ich im Voice-Over etwas rausschneide,
    // dann rutscht die obere Seite mit zusammen … weil der Übergang genau an
    // der Stelle ist“). Bis hierher legte jede Änderung die Hauptspur neu --
    // auch ein Schnitt im Ton. Stand sie nicht genau so, wie der Magnet sie
    // legen würde, sprang sie dabei.
    const basisVorher = vorher.tracks.find((t) => t.id === basis.id);
    if (basisVorher &&
        vorher.settings.magnetisch &&
        vorher.tracks.find((t) => t.kind === 'video')?.id === basis.id &&
        gleicheHauptspur(basisVorher.clips, basis.clips)) {
        return [];
    }
    const runde = (x) => Math.round(x * 1e6) / 1e6;
    // Die Hauptspur, lückenlos.
    const neu = new Map();
    let ende = 0;
    let vorige = null;
    for (const c of inReihe(basis.clips)) {
        const blende = vorige && c.transitionIn && c.transitionIn.duration > 0 ? Math.min(c.transitionIn.duration, vorige.duration, c.duration) : 0;
        const start = runde(Math.max(0, ende - blende));
        neu.set(c.id, start);
        ende = start + c.duration;
        vorige = c;
    }
    const ops = [];
    for (const c of basis.clips) {
        const ziel = neu.get(c.id);
        if (Math.abs(ziel - c.start) > 1e-6)
            ops.push({ op: 'moveClip', clipId: c.id, toTrackId: basis.id, start: ziel });
    }
    // Was an einem Clip der Hauptspur hing, wandert mit ihm.
    if (!basisVorher)
        return ops;
    const traeger = inReihe(basisVorher.clips);
    const tragendBei = (zeit) => [...traeger].reverse().find((b) => b.start <= zeit + 1e-6 && zeit < b.start + b.duration - 1e-6);
    for (const spur of nachher.tracks) {
        if (spur.id === basis.id || spur.locked)
            continue;
        const spurVorher = vorher.tracks.find((t) => t.id === spur.id);
        if (!spurVorher)
            continue;
        for (const c of spur.clips) {
            const alt = spurVorher.clips.find((x) => x.id === c.id);
            // Neu, oder von der Änderung selbst bewegt: dann bleibt er, wo er ist.
            if (!alt || Math.abs(alt.start - c.start) > 1e-6)
                continue;
            const b = tragendBei(alt.start);
            const bNeu = b ? neu.get(b.id) : undefined;
            if (!b || bNeu === undefined)
                continue;
            const um = bNeu - b.start;
            if (Math.abs(um) <= 1e-6)
                continue;
            ops.push({ op: 'moveClip', clipId: c.id, toTrackId: spur.id, start: runde(Math.max(0, c.start + um)) });
        }
    }
    return ops;
}
/**
 * Die Reihenfolge der Hauptspur. Fangen zwei Clips im selben Augenblick an,
 * zuerst der, der früher aufhört: das passiert, wenn eine Blende den ganzen
 * vorigen Clip verschluckt (sie ist nie länger als er, aber genauso lang
 * kann sie sein). Nach der Kennung allein (`sortedClips`) entschied ein
 * Zufall, und der nächste Durchgang legte die beiden andersherum -- die
 * Hauptspur sprang bei einer Änderung, die sie gar nicht betraf.
 */
function inReihe(clips) {
    // „Im selben Augenblick“ heisst: bis auf die Rundung des Magneten.
    const gleich = (x, y) => Math.abs(x - y) <= 1e-6;
    return [...clips].sort((a, b) => !gleich(a.start, b.start)
        ? a.start - b.start
        : !gleich(a.start + a.duration, b.start + b.duration)
            ? a.start + a.duration - (b.start + b.duration)
            : a.id.localeCompare(b.id));
}
/** Dieselben Clips an denselben Stellen, gleich lang, mit denselben Blenden. */
function gleicheHauptspur(a, b) {
    if (a.length !== b.length)
        return false;
    const vorher = new Map(a.map((c) => [c.id, c]));
    return b.every((c) => {
        const v = vorher.get(c.id);
        return (v !== undefined &&
            Math.abs(v.start - c.start) <= 1e-9 &&
            Math.abs(v.duration - c.duration) <= 1e-9 &&
            (v.transitionIn?.duration ?? 0) === (c.transitionIn?.duration ?? 0));
    });
}
//# sourceMappingURL=magnet.js.map